import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, map } from 'rxjs/operators';

import { ThemeService } from '../../../core/services/theme.service';
import { AuthService } from '../../../services/auth.service';
import { AvatarApiService } from '../../../services/avatar-api.service';
import type { SectorType } from '../../../models/sectorType.model';
import { NavigationStateService } from '../../services/navigation-state.service';
import { BottomNavComponent } from '../bottom-nav/bottom-nav.component';

const SECTORS: readonly SectorType[] = ['A', 'B', 'C', 'D', 'E'];

const SECTOR_ICONS: Record<SectorType, string> = {
  A: 'handshake',
  B: 'fitness_center',
  C: 'healing',
  D: 'monitor_heart',
  E: 'sports_martial_arts',
};

@Component({
  selector: 'app-layout',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTooltipModule,
    BottomNavComponent,
  ],
  templateUrl: './layout.html',
  styleUrls: ['./layout.scss'],
})
export class LayoutComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly themeService = inject(ThemeService);
  private readonly authService = inject(AuthService);
  private readonly avatarApi = inject(AvatarApiService);

  protected readonly navigationState = inject(NavigationStateService);

  readonly sidenav = viewChild<MatSidenav>('sidenav');

  protected readonly sectors = SECTORS;
  protected readonly sectorIcons = SECTOR_ICONS;

  protected readonly isMobile = toSignal(
    this.breakpoints.observe('(max-width: 959.98px)').pipe(map((r) => r.matches)),
    { initialValue: false },
  );

  protected readonly isCollapsed = signal<boolean>(false);

  protected readonly isAuthPage = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects === '/login' || e.urlAfterRedirects === '/register'),
    ),
    { initialValue: this.router.url === '/login' || this.router.url === '/register' },
  );

  protected readonly isDark = this.themeService.isDark;
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly currentUser = this.authService.currentUser;
  protected readonly hasAvatar = signal(false);

  protected readonly userAvatarUrl = computed(() => {
    const user = this.currentUser();
    return user ? this.avatarApi.getAvatarUrl(user.guid) : undefined;
  });

  protected readonly userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return '';
    return user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  });

  constructor() {
    effect(() => {
      const user = this.currentUser();
      if (!user) {
        this.hasAvatar.set(false);
        return;
      }
      this.checkAvatarExists(user.guid);
    });

    this.breakpoints
      .observe('(max-width: 959.98px)')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.matches) {
          this.isCollapsed.set(false);
        }
      });
  }

  toggleMenu(): void {
    this.sidenav()?.toggle();
  }

  toggleCollapse(): void {
    this.isCollapsed.update((v) => !v);
  }

  closeDrawerIfMobile(): void {
    if (this.isMobile()) {
      this.sidenav()?.close();
    }
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  logout(): void {
    this.authService.logout().subscribe(() => this.router.navigate(['/login']));
  }

  private checkAvatarExists(userId: string): void {
    const img = new Image();
    img.onload = () => this.hasAvatar.set(true);
    img.onerror = () => this.hasAvatar.set(false);
    img.src = this.avatarApi.getAvatarUrl(userId);
  }
}
